import React from 'react';
import { Form, Formik, FormikProps } from 'formik';
import countries from 'country-list';
import * as Yup from 'yup';
import * as _ from 'lodash';

import * as Edge from '../../core';
import FormField from '../formField';
import Input from '../../components/global/input';
import './index.scss';
import { ReactComponent as Glasses } from '../../assets/images/icons/glasses.svg';
import Error from '../global/error';
import Loading from '../loading';
import {
	AcknowledgeSubscriptionFields,
	acknowledgeSubscriptionFieldValidations,
	AcknowledgeSubscriptionFieldValues,
} from '../createAccountForm/acknowledgeSubscriptionFields';

export interface PurchaseGlassesFormProps {
	initialValues?: Partial<GlassesOptionFormValues>;
	showRecurringSubscriptionFields: boolean;
	onSubmit: (values: GlassesOptionFormValues) => Promise<void>;
	productRenewalMonths?: number;
}

export interface GlassesOptionFormValues extends AcknowledgeSubscriptionFieldValues {
	glassesNumber: string;
	country: string;
}

const glassesOptionFormSchema = (isRecurring: boolean) =>
	Yup.object().shape({
		glassesNumber: Yup.number().required('Number of glasses is required'),
		country: Yup.string().when('glassesNumber', {
			is: (value: number) => value === 0,
			then: Yup.string().notRequired(),
			otherwise: Yup.string().required('Country is required.'),
		}),
		...(isRecurring ? acknowledgeSubscriptionFieldValidations : {}),
	});

const countryList = _.orderBy(countries.getData(), (i) => i.code === Edge.Constants.US_COUNTRY_CODE, 'desc');

function PurchaseGlasses({
	initialValues,
	showRecurringSubscriptionFields,
	onSubmit,
	productRenewalMonths,
}: PurchaseGlassesFormProps) {
	return (
		<div className="purchaseGlasses">
			<h3 className="cardTitle">Order Vizual Edge 3D Glasses</h3>
			<p>
				Vizual Edge 3D glasses are specific to our software and are <b>required for use</b>. Regular 3D glasses
				are not compatible with our software!
			</p>
			<Formik
				initialValues={_.merge(
					{},
					{
						glassesNumber: '',
						country: Edge.Constants.US_COUNTRY_CODE,
					},
					showRecurringSubscriptionFields
						? {
								acknowledgeSubscription: false,
								acknowledgeSubscriptionStart: false,
								acknowledgeCancellation: false,
								acknowledgeRefundPolicy: false,
						  }
						: {},
					initialValues || {}
				)}
				validationSchema={glassesOptionFormSchema(showRecurringSubscriptionFields)}
				onSubmit={async (values, actions) => {
					actions.setStatus(undefined);
					actions.setSubmitting(true);
					try {
						await onSubmit(values);
					} catch (e) {
						actions.setStatus(Edge.API.getErrorMessage(e));
					}
					actions.setSubmitting(false);
				}}
				render={(props: FormikProps<GlassesOptionFormValues>) => (
					<Form>
						<div className="icon">
							<Glasses />
						</div>
						<FormField
							component="input"
							type="number"
							name="glassesNumber"
							label="Number of Glasses"
							min={0}
						/>
						{props.values.glassesNumber !== undefined &&
							parseInt(props.values.glassesNumber, 10) > 0 &&
							renderCountryList()}
						{props.status && <Error>{props.status}</Error>}
						{props.isSubmitting && <Loading buttonLoader />}
						{showRecurringSubscriptionFields && productRenewalMonths && (
							<AcknowledgeSubscriptionFields renewalMonths={productRenewalMonths} />
						)}
						<Input type="submit" disabled={!props.isValid || props.isSubmitting} value="submit" />
					</Form>
				)}
			/>
		</div>
	);
}

const renderCountryList = (): JSX.Element => {
	return (
		<>
			<h3 className={'cardTitle'}>SELECT SHIPPING DESTINATION (COUNTRY)</h3>

			<FormField component="select" name={'country'} label="Country">
				{countryList.map((c: { code: string; name: string }, ix: number) => {
					return (
						<option key={ix} value={c.code}>
							{c.name}
						</option>
					);
				})}
			</FormField>
		</>
	);
};

export default PurchaseGlasses;
